import {
    Box,
    Button,
    Card,
    Container,
    Divider,
    Group,
    List,
    SimpleGrid,
    Stack,
    Text,
    ThemeIcon,
    Title,
} from '@mantine/core';
import {
    IconArrowRight,
    IconCheck,
    IconChefHat,
    IconGymnastics,
    IconHeart,
    IconHome2,
    IconPill,
    IconStethoscope,
    IconUsers,
} from '@tabler/icons-react';
import { Link } from 'react-router';

const services = [
  {
    icon: IconHeart,
    title: 'Activities of Daily Living',
    description: 'Compassionate support with everyday tasks while promoting independence and dignity.',
    features: [
      'Bathing, showering, and grooming',
      'Dressing and personal hygiene',
      'Eating and meal assistance',
      'Mobility, walking, and transfers',
      'Toileting and continence care',
      'Personalized care plans',
    ],
  },
  {
    icon: IconHome2,
    title: 'Instrumental Activities of Daily Living',
    description: 'Help with the tasks that support independent living and daily routines.',
    features: [
      'Meal preparation and cooking',
      'Laundry and housekeeping',
      'Transportation and errands',
      'Managing finances and bills',
      'Scheduling appointments',
      'Phone and technology assistance',
    ],
  },
  {
    icon: IconStethoscope,
    title: '24/7 Nursing Care',
    description: 'Round-the-clock professional nurse supervision and support (in-person during business hours and online during off-hours).',
    features: [
      'Licensed nursing staff on-site',
      'Health monitoring and assessments',
      'Coordination with physicians',
      'Emergency response protocols',
      'Chronic condition management',
    ],
  },
  {
    icon: IconPill,
    title: 'Medication Management',
    description: 'Comprehensive medication oversight to ensure safety and compliance.',
    features: [
      'Medication administration',
      'Prescription coordination',
      'Pharmacy partnerships',
      'Side effect monitoring',
      'Family communication',
    ],
  },
  {
    icon: IconChefHat,
    title: 'Dining Services',
    description: 'Delicious, nutritious meals prepared by our culinary team.',
    features: [
      'Three chef-prepared meals daily',
      'Healthy snacks available',
      'Special dietary accommodations',
      'Restaurant-style dining room',
      'Private dining for family visits',
    ],
  },
  {
    icon: IconHome2,
    title: 'Housekeeping & Laundry',
    description: 'Complete housekeeping services so residents can focus on enjoying life.',
    features: [
      'Daily room tidying',
      'Weekly deep cleaning',
      'Personal laundry service',
      'Linen service included',
      'Maintenance support',
    ],
  },
  {
    icon: IconUsers,
    title: 'Social Programs',
    description: 'Engaging activities that nurture connections and bring joy.',
    features: [
      'Daily group activities',
      'Cultural and entertainment events',
      'Family engagement services',
      'Outdoor excursions',
      'Volunteer programs',
    ],
  },
  {
    icon: IconGymnastics,
    title: 'Wellness & Fitness',
    description: 'Programs designed to maintain physical health and vitality.',
    features: [
      'Exercise classes',
      'Physical therapy coordination',
      'Balance and fall prevention',
      'Walking groups',
      'Wellness assessments',
    ],
  },
];

export function ServicesPage() {
  return (
    <>
      {/* Hero Section */}
      <Box py={80} bg="gray.0">
        <Container size="xl">
          <Stack align="center" gap="md" maw={700} mx="auto">
            <Text c="harmony.6" fw={600} tt="uppercase" size="sm">
              Our Services
            </Text>
            <Title ta="center">
              Comprehensive Care for Every Need
            </Title>
            <Text c="dimmed" ta="center" size="lg">
              At Hillside Harmony, we offer a full spectrum of services designed to support
              our residents' physical, emotional, and social well-being. Every service is
              delivered with compassion and tailored to individual needs.
            </Text>
          </Stack>
        </Container>
      </Box>

      {/* Services Grid */}
      <Box py={80}>
        <Container size="xl">
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
            {services.map((service) => (
              <Card key={service.title} padding="xl" shadow="sm" radius="lg">
                <Group gap="md" mb="md" wrap="nowrap">
                  <ThemeIcon size={50} radius="md" variant="light" color="harmony" style={{ flexShrink: 0 }}>
                    <service.icon size={26} stroke={1.5} />
                  </ThemeIcon>
                  <Box>
                    <Text fw={600} size="lg">
                      {service.title}
                    </Text>
                    <Text c="dimmed" size="sm">
                      {service.description}
                    </Text>
                  </Box>
                </Group>
                <Divider mb="md" />
                <List
                  spacing="xs"
                  size="sm"
                  icon={
                    <ThemeIcon size={20} radius="xl" color="harmony" variant="light">
                      <IconCheck size={12} stroke={3} />
                    </ThemeIcon>
                  }
                >
                  {service.features.map((feature) => (
                    <List.Item key={feature}>{feature}</List.Item>
                  ))}
                </List>
              </Card>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box py={80} bg="harmony.7">
        <Container size="md">
          <Stack align="center" gap="lg">
            <Title order={2} c="white" ta="center">
              Have Questions About Our Services?
            </Title>
            <Text c="white" ta="center" size="lg" maw={500} opacity={0.9}>
              Our team is here to help you understand how we can meet your family's
              unique needs. Contact us for a personalized consultation.
            </Text>
            <Group gap="md">
              <Button
                component={Link}
                to="/contact"
                size="lg"
                color="white"
                c="harmony.7"
                rightSection={<IconArrowRight size={18} />}
              >
                Contact Us
              </Button>
              <Button
                component="a"
                href="tel:+16514000063"
                size="lg"
                variant="outline"
                color="white"
              >
                (651) 400-0063
              </Button>
            </Group>
          </Stack>
        </Container>
      </Box>
    </>
  );
}
